import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { TERMINAL as MIGRATION_SETTLED } from '../contacts/ownerOperationsApi';
import { lmError, newIdempotencyKey } from './lmApi';
import { GhostButton, PrimaryButton } from './LmBits';
import { BLOCK_POOL_FILTERS, blockLabel } from './lmColumns';

/**
 * Admin-confirmed Commit (#773 LM-05, #774 LM-06).
 *
 * 1. Commit preview: selected / eligible / blocked-by-reason counts, creates,
 *    updates, links, relationships, overwrites and per-rep totals.
 * 2. Confirm: one idempotency key per dialog, so a double-click commits once.
 * 3. Progress: polls the job (a few transient poll failures are retried with
 *    backoff); Cancel while it runs; the dialog may be closed — the job keeps
 *    running and is tracked in Import History.
 * 4. Outcomes: committed / failed, with Retry for retryable rows.
 *
 * Owner-migration rows (#862) are never committed from here: the preview shows
 * the migration categories and the action is "Request approval" (a reason is
 * required, and it stays disabled while any row of the part is an exception).
 * A different admin approves in Import History; the dialog then follows the
 * parts as they commit.
 */
const TERMINAL = new Set(['completed', 'partial', 'failed', 'cancelled', 'interrupted', 'reverted', 'reverted_with_conflicts']);
const MIGRATION_STATUS = {
    pending_approval: 'Waiting for a different admin to approve',
    approved: 'Approved — starting the parts',
    committing: 'Committing parts',
    committed: 'Committed',
    partially_committed: 'Partially committed',
    rejected: 'Rejected',
    expired: 'Approval expired',
};

function Stat({ label, value, tone }) {
    return (
        <div className={`rounded-[11px] px-3.5 py-3 border ${tone === 'warn' ? 'bg-amber-50 dark:bg-warn-bg border-amber-200 dark:border-border' : 'bg-muted/50 border-border'}`}>
            <div className={`text-[11px] font-semibold tracking-[0.05em] ${tone === 'warn' ? 'text-ink-2' : 'text-muted-foreground'}`}>{label}</div>
            <div className="mt-1 text-[19px] font-semibold text-foreground tabular-nums">{value}</div>
        </div>
    );
}

/**
 * One blocked reason (#773 QA): collapsed by default; expanded it names the
 * rows (up to the preview's 50) and, past that, counts the rest and links to
 * For Pooling filtered to them where a table filter can express the reason.
 */
function BlockedReason({ reason, onShowBlocked }) {
    const [open, setOpen] = useState(false);
    const rows = reason.rows || [];
    const more = Math.max(0, reason.count - rows.length);
    const filter = BLOCK_POOL_FILTERS[reason.code];
    const Chevron = open ? ChevronDown : ChevronRight;
    const id = `lm-blocked-${reason.code}`;
    return (
        <li className="rounded-[10px] border border-amber-200 dark:border-border bg-amber-50 dark:bg-warn-bg" data-testid="lm-blocked-reason">
            <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={id}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] text-amber-800 dark:text-warn">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span className="flex-1">{reason.count} × {blockLabel(reason.code)}</span>
                <Chevron className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            </button>
            {open && (
                <div id={id} className="px-3 pb-2.5">
                    <ul className="max-h-[180px] overflow-y-auto divide-y divide-amber-200 dark:divide-border border-t border-amber-200 dark:border-border">
                        {rows.map((r) => (
                            <li key={r.row_id} className="py-1.5 text-[12.5px] text-foreground" data-testid="lm-blocked-row">
                                <span className="font-medium text-foreground">{r.name}</span>
                                {r.company ? <span className="text-ink-2"> · {r.company}</span> : null}
                                {r.owner_name ? <span className="text-ink-2"> · {r.owner_name}</span> : null}
                            </li>
                        ))}
                    </ul>
                    {more > 0 && (
                        <div className="pt-1.5 text-[12px] text-amber-800 dark:text-warn" data-testid="lm-blocked-more">
                            and {more.toLocaleString()} more
                            {onShowBlocked && (
                                <>{' · '}
                                    <button type="button" className="font-semibold text-violet-700 dark:text-violet-300 hover:underline"
                                            onClick={() => onShowBlocked(reason.code, filter)}>
                                        {filter ? `Show them in For Pooling (${filter.label})` : 'Open For Pooling'}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}

export default function LmCommitDialog({ open, onClose, api, selection, onFinished, onViewHistory, onShowBlocked,
                                         canMigrateOwners = false, pollMs = 1500 }) {
    const [phase, setPhase] = useState('loading');
    const [preview, setPreview] = useState(null);
    const [job, setJob] = useState(null);
    const [error, setError] = useState(null);
    const [reason, setReason] = useState('');
    const [migrationOp, setMigrationOp] = useState(null);
    const [requesting, setRequesting] = useState(false);
    const keyRef = useRef(null);
    const timerRef = useRef(null);
    const aliveRef = useRef(true);  // a poll in flight at unmount must not re-arm
    const missesRef = useRef(0);

    const stopPolling = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = null; };
    useEffect(() => () => { aliveRef.current = false; stopPolling(); }, []);

    useEffect(() => {
        if (!open) { stopPolling(); return undefined; }
        aliveRef.current = true;
        keyRef.current = newIdempotencyKey();
        setPhase('loading'); setPreview(null); setJob(null); setError(null); setReason(''); setMigrationOp(null);
        setRequesting(false);
        api.commitPreview(selection)
            .then((pv) => { setPreview(pv); setPhase('preview'); })
            .catch((err) => { setError(lmError(err, 'The commit preview could not be built.')); setPhase('error'); });
        return stopPolling;
    }, [open, api, selection]);

    const poll = useCallback((jobId, delay = pollMs) => {
        stopPolling();
        timerRef.current = setTimeout(async () => {
            if (!aliveRef.current) return;
            try {
                const res = await api.job(jobId, { page_size: 100, outcome: 'retryable_error' });
                if (!aliveRef.current) return;
                missesRef.current = 0;
                setJob({ ...res.job, failedRows: res.rows?.data || [] });
                if (TERMINAL.has(res.job.status)) {
                    setPhase('done');
                    onFinished?.(res.job);
                } else {
                    poll(jobId, Math.min(delay * 1.5, 8000));
                }
            } catch (err) {
                if (!aliveRef.current) return;
                const status = err?.response?.status;
                missesRef.current += 1;
                // The job runs server-side regardless; a network blip or 5xx must
                // not abandon tracking. Give up only after several misses.
                if ((!status || status >= 500) && missesRef.current < 5) {
                    poll(jobId, Math.min(delay * 2, 15000));
                    return;
                }
                setError(lmError(err, 'Lost track of the commit job. Check Import History.'));
                setPhase('error');
            }
        }, delay);
    }, [api, onFinished, pollMs]);

    // After "Request approval": follow the parent until its parts settle.
    const pollMigration = useCallback((operationId, delay = pollMs) => {
        stopPolling();
        timerRef.current = setTimeout(async () => {
            if (!aliveRef.current) return;
            try {
                const op = await api.ownerMigration(operationId);
                if (!aliveRef.current) return;
                missesRef.current = 0;
                setMigrationOp(op);
                if (MIGRATION_SETTLED.has(op.status)) onFinished?.(op);
                else pollMigration(operationId, Math.min(delay * 1.5, 15000));
            } catch (err) {
                if (!aliveRef.current) return;
                missesRef.current += 1;
                const status = err?.response?.status;
                if ((!status || status >= 500) && missesRef.current < 5) pollMigration(operationId, Math.min(delay * 2, 15000));
            }
        }, delay);
    }, [api, onFinished, pollMs]);

    const requestApproval = async () => {
        const operationId = preview?.migration?.parent_operation_id;
        setError(null);
        setRequesting(true);
        try {
            const res = await api.requestMigrationApproval(operationId, reason.trim());
            setMigrationOp(res.operation);
            setPhase('requested');
            pollMigration(operationId);
        } catch (err) {
            setError(lmError(err, 'The migration could not be sent for approval.'));
        } finally {
            setRequesting(false);
        }
    };

    const confirm = async () => {
        setPhase('running'); setError(null);
        try {
            const res = await api.commit(preview.preview_id, keyRef.current);
            setJob(res.job);
            if (TERMINAL.has(res.job.status)) { setPhase('done'); onFinished?.(res.job); } else poll(res.job.job_id, Math.min(400, pollMs));
        } catch (err) {
            setError(lmError(err, 'The commit could not start.'));
            setPhase('error');
        }
    };

    const cancel = async () => {
        setError(null);
        try { await api.cancelJob(job.job_id); } catch (err) { setError(lmError(err, 'Could not cancel the job.')); }
    };

    const retry = async () => {
        setError(null);
        try {
            const res = await api.retryJob(job.job_id);
            missesRef.current = 0;
            setJob(res.job); setPhase('running'); poll(res.job.job_id, Math.min(400, pollMs));
        } catch (err) {
            setError(lmError(err, 'Nothing could be retried.'));
        }
    };

    const close = () => { stopPolling(); onClose?.(); };
    const inlineError = error && phase !== 'error' && (
        <p role="alert" className="text-[12.5px] text-red-800 dark:text-bad" data-testid="lm-commit-inline-error">{error}</p>
    );

    const counts = preview?.counts || {};
    const jc = job?.counts || {};
    const mc = preview?.migration;
    const isMigration = preview?.mode === 'owner_migration';
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
            <DialogContent className="sm:max-w-[640px]" data-testid="lm-commit-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">
                        {isMigration ? 'Owner migration' : 'Commit to CRM'}
                    </DialogTitle>
                    <DialogDescription className="text-[13px] text-muted-foreground">
                        {isMigration
                            ? 'Moves existing contacts to the owners in the sheet, once a different admin approves.'
                            : 'Commit writes Companies, Contacts and Relationships to the CRM. Rows that fail stay in For Pooling, retryable.'}
                    </DialogDescription>
                </DialogHeader>

                {phase === 'loading' && <div className="py-8 flex items-center justify-center gap-2 text-[13px] text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Checking every selected row…</div>}

                {phase === 'error' && (
                    <div role="alert" className="space-y-3">
                        <p className="text-[13px] text-red-800 dark:text-bad">{error}</p>
                        <div className="flex justify-end"><GhostButton onClick={onClose}>Close</GhostButton></div>
                    </div>
                )}

                {phase === 'preview' && preview && isMigration && (
                    <div className="space-y-4" data-testid="lm-commit-migration-preview">
                        <p className="text-[12.5px] text-ink-2">
                            One-time owner migration: these rows only change the owner of existing contacts, and only after a different admin approves.
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            <Stat label="MATCHED" value={mc.matched ?? 0} />
                            <Stat label="WILL CHANGE" value={mc.changed ?? 0} />
                            <Stat label="ALREADY CORRECT" value={mc.already_correct ?? 0} />
                            <Stat label="EXCEPTIONS" value={preview.blocked_total} tone={preview.blocked_total ? 'warn' : undefined} />
                        </div>
                        {mc.stale_owner > 0 && (
                            <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-warn-bg text-[12.5px] text-amber-800 dark:text-warn" data-testid="lm-migration-stale">
                                {mc.stale_owner} contact{mc.stale_owner === 1 ? '' : 's'} changed owner since this part was staged; they will be reported as conflicts, not changed.
                            </div>
                        )}
                        {preview.blocked.length > 0 && (
                            <ul className="space-y-1.5" aria-label="Blocked rows">
                                {preview.blocked.map((b) => <BlockedReason key={b.code} reason={b} onShowBlocked={onShowBlocked} />)}
                            </ul>
                        )}
                        {preview.per_rep.length > 0 && (
                            <div>
                                <div className="text-[12px] font-semibold tracking-[0.05em] text-muted-foreground">BY NEW CONTACT OWNER</div>
                                <ul className="mt-1 text-[12.5px] text-foreground">
                                    {preview.per_rep.map((r) => (
                                        <li key={r.owner_id}>{r.name || r.owner_id}: {r.contacts} contact{r.contacts === 1 ? '' : 's'}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <label className="flex flex-col gap-1 text-[12.5px] text-foreground">Reason for the approver
                            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
                                   aria-label="Approval reason" data-testid="lm-migration-reason"
                                   className="h-[34px] px-2 border border-input rounded-lg bg-card text-foreground" />
                        </label>
                        {preview.migration_blocked && (
                            <p className="text-[12.5px] text-amber-800 dark:text-warn">Resolve or skip every exception in Staging before asking for approval.</p>
                        )}
                        {inlineError}
                        <div className="flex justify-end gap-2">
                            <GhostButton onClick={onClose}>Cancel</GhostButton>
                            <PrimaryButton tone="dark" onClick={requestApproval} testId="lm-migration-request"
                                           disabled={requesting || !canMigrateOwners || preview.migration_blocked || !reason.trim() || !mc.parent_operation_id}>
                                {requesting ? 'Sending for approval…' : 'Request approval'}
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {phase === 'requested' && migrationOp && (
                    <div className="space-y-3" aria-live="polite" data-testid="lm-migration-requested">
                        <p className="text-[13px] text-foreground font-semibold">{MIGRATION_STATUS[migrationOp.status] || migrationOp.status?.replace(/_/g, ' ')}</p>
                        <p className="text-[12.5px] text-muted-foreground">
                            A different admin with migration rights approves or rejects it in Import History. You can close this.
                        </p>
                        {(migrationOp.parts || []).length > 0 && (
                            <ul className="text-[12.5px] text-foreground" data-testid="lm-migration-parts">
                                {migrationOp.parts.map((p) => (
                                    <li key={p.part_index}>Part {p.part_index}: {String(p.status || 'pending').replace(/_/g, ' ')}</li>
                                ))}
                            </ul>
                        )}
                        <div className="flex justify-end gap-2">
                            {onViewHistory && <GhostButton onClick={onViewHistory}>View in Import History</GhostButton>}
                            <GhostButton onClick={close}>Close</GhostButton>
                        </div>
                    </div>
                )}

                {phase === 'preview' && preview && !isMigration && (
                    <div className="space-y-4" data-testid="lm-commit-preview">
                        <div className="grid grid-cols-3 gap-2.5">
                            <Stat label="SELECTED" value={preview.selected} />
                            <Stat label="READY TO COMMIT" value={preview.eligible} />
                            <Stat label="BLOCKED" value={preview.blocked_total} tone={preview.blocked_total ? 'warn' : undefined} />
                        </div>
                        {preview.blocked.length > 0 && (
                            <ul className="space-y-1.5" aria-label="Blocked rows">
                                {preview.blocked.map((b) => <BlockedReason key={b.code} reason={b} onShowBlocked={onShowBlocked} />)}
                            </ul>
                        )}
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12.5px] text-foreground">
                            <div>New companies <span className="float-right tabular-nums font-semibold">{counts.companies_create || 0}</span></div>
                            <div>New contacts <span className="float-right tabular-nums font-semibold">{counts.contacts_create || 0}</span></div>
                            <div>Companies updated <span className="float-right tabular-nums font-semibold">{counts.companies_update || 0}</span></div>
                            <div>Contacts updated <span className="float-right tabular-nums font-semibold">{counts.contacts_update || 0}</span></div>
                            <div>Linked to existing companies <span className="float-right tabular-nums font-semibold">{counts.companies_link || 0}</span></div>
                            <div>Relationships <span className="float-right tabular-nums font-semibold">{counts.relationships || 0}</span></div>
                        </div>
                        {counts.overwrites > 0 && (
                            <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-warn-bg text-[12.5px] text-amber-800 dark:text-warn">
                                {counts.overwrites} existing CRM value{counts.overwrites === 1 ? '' : 's'} will be overwritten (overwrite policy).
                            </div>
                        )}
                        {preview.per_rep.length > 0 && (
                            <div>
                                <div className="text-[12px] font-semibold tracking-[0.05em] text-muted-foreground">BY OWNER</div>
                                <ul className="mt-1 text-[12.5px] text-foreground">
                                    {preview.per_rep.map((r) => (
                                        <li key={r.owner_id}>{r.name || r.owner_id}: {r.companies} companies · {r.contacts} contacts</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <GhostButton onClick={onClose}>Cancel</GhostButton>
                            <PrimaryButton tone="dark" onClick={confirm} disabled={!preview.eligible} testId="lm-commit-confirm">
                                Commit {preview.eligible} record{preview.eligible === 1 ? '' : 's'}
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {phase === 'running' && (
                    <div className="py-6 text-center" aria-live="polite" data-testid="lm-commit-running">
                        <Loader2 className="w-5 h-5 mx-auto animate-spin text-muted-foreground" aria-hidden="true" />
                        <p className="mt-2 text-[13px] text-ink-2">Committing… {job?.status ? `(${job.status})` : ''}</p>
                        <p className="mt-1 text-[12px] text-muted-foreground">You can close this — the commit keeps running and shows in Import History.</p>
                        {inlineError}
                        {job?.job_id && (
                            <div className="mt-3 flex justify-center gap-2">
                                <GhostButton onClick={cancel} testId="lm-commit-cancel">Cancel remaining rows</GhostButton>
                                <GhostButton onClick={close}>Close</GhostButton>
                            </div>
                        )}
                    </div>
                )}

                {phase === 'done' && job && (
                    <div className="space-y-3" aria-live="polite" data-testid="lm-commit-done">
                        <div className="flex gap-4 text-[13px]">
                            <span className="inline-flex items-center gap-1.5 text-emerald-800 dark:text-good"><CheckCircle2 className="w-4 h-4" />{jc.committed ?? 0} committed</span>
                            <span className="inline-flex items-center gap-1.5 text-red-800 dark:text-bad"><XCircle className="w-4 h-4" />{(jc.retryable_error || 0) + (jc.permanent_error || 0)} failed</span>
                            {jc.returned_to_staging ? <span className="text-amber-800 dark:text-warn">{jc.returned_to_staging} returned to Staging</span> : null}
                        </div>
                        <p className="text-[12.5px] text-muted-foreground">Job status: {job.status.replace(/_/g, ' ')}.</p>
                        {inlineError}
                        {(job.failedRows || []).length > 0 && (
                            <ul className="max-h-[200px] overflow-y-auto border border-border rounded-[10px] divide-y divide-border/60">
                                {job.failedRows.map((r) => (
                                    <li key={r.row_id} className="px-3 py-2 text-[12.5px]">
                                        <div className="font-medium text-foreground">{`${r.staged_values?.first_name || ''} ${r.staged_values?.last_name || ''}`.trim() || r.staged_values?.company_name}</div>
                                        <div className="text-muted-foreground">{r.commit?.error?.message}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex justify-end gap-2">
                            {onViewHistory && <GhostButton onClick={onViewHistory}>View in Import History</GhostButton>}
                            {(jc.retryable_error || 0) > 0 && <PrimaryButton tone="dark" onClick={retry} testId="lm-commit-retry">Retry {jc.retryable_error} failed</PrimaryButton>}
                            <GhostButton onClick={onClose}>Close</GhostButton>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
